import React, { useContext, useEffect, useState } from "react";
import styles from "./CreateAudience.module.css";
import { FaUsers, FaFilter, FaSave, FaSort } from "react-icons/fa";
import { audienceData } from "./AudiencesFormat";
import {
    MdCheckBoxOutlineBlank,
    MdClose,
    MdOutlineCheckBox,
    MdRadioButtonChecked,
    MdRadioButtonUnchecked,
} from "react-icons/md";
import EachAudienceCategory from "../../Components/Communications/Audiences/EachAudienceCategory";
import { IoMdCheckboxOutline } from "react-icons/io";
import { BASE_URL_API } from "../../References/URLs";
import AuthContext from "../../store/auth-context";
import ActiveStoreContext from "../../store/ActiveStoreContext";
import { Link, useSearchParams } from "react-router-dom";
import Select from "react-select";
import SaveAudience from "../../Components/Communications/Audiences/SaveAudience";
import { AnimatePresence, motion } from "framer-motion";
import { IoChevronDownOutline, IoChevronUpOutline } from "react-icons/io5";
import Papa from "papaparse";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";

// Matches .searchBox (CreateAudience.module.css) exactly — same border
// color/radius/font-size, same fixed 34px height — so the rows-per-page
// control reads as one finished toolbar with the search box instead of
// two mismatched pickers, on both mobile and desktop. .searchBox pins its
// height with !important, so this has to match that literal 34px rather
// than a viewport-relative unit that can drift from it.
const rowsPerPageSelectStyles = {
    container: (base) => ({ ...base, minWidth: 92, flex: 'none' }),
    control: (base, state) => ({
        ...base,
        minHeight: '34px',
        height: '34px',
        borderRadius: '8px',
        border: `1px solid ${state.isFocused ? '#007bff' : '#dcdfe4'}`,
        boxShadow: 'none',
        background: 'white',
        cursor: 'pointer',
        fontSize: '0.82rem',
    }),
    valueContainer: (base) => ({ ...base, height: '32px', padding: '0 4px 0 10px' }),
    singleValue: (base) => ({ ...base, color: '#333' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorSeparator: () => ({ display: 'none' }),
    indicatorsContainer: (base) => ({ ...base, height: '32px' }),
    dropdownIndicator: (base) => ({ ...base, padding: '0 8px', color: '#94a3b8' }),
    menu: (base) => ({ ...base, borderRadius: '8px', border: '1px solid #dcdfe4', boxShadow: '0 4px 16px rgba(0,0,0,0.08)', zIndex: 20 }),
    menuList: (base) => ({ ...base, padding: '4px', maxHeight: '220px' }),
    option: (base, state) => ({
        ...base,
        borderRadius: '6px',
        fontSize: '0.8rem',
        padding: '6px 10px',
        color: state.isSelected ? '#fff' : '#333',
        background: state.isSelected ? '#007bff' : state.isFocused ? '#eff6ff' : 'transparent',
        cursor: 'pointer',
    }),
}

const CreateAudience = () => {
    const authCtx = useContext(AuthContext);
    // Reached from CampaignAudience.js's "Create New Audience" with
    // ?redirect=/communications/campaign/:id/audience — same param SaveAudience.js
    // already reads to send the user back once they're done. The header
    // button reflects that: "Go To Campaign" back to the campaign that sent
    // them here, instead of the generic "Create Campaign" entry point.
    const [searchParams] = useSearchParams();
    const redirectPath = searchParams.get('redirect');
    const [currentlySelectedSubCategory, setCurrentlySelectedSubCategory] = useState(null);
    const asCtx = useContext(ActiveStoreContext);
    const activeStore = (() => {
        try {
            return asCtx?.activeStore && asCtx.activeStore.trim() !== ""
                ? JSON.parse(asCtx.activeStore)
                : null;
        } catch {
            return null;
        }
    })();
    const [isOpen, setIsOpen] = useState(false)
    const subCategorySelectHandler = (k) => {
        setCurrentlySelectedSubCategory(k);
    };
    // Mobile-only bottom sheet for the audience source picker (see the
    // mobile media query in CreateAudience.module.css) — purely UI state,
    // same role as isOpen/filtersOpen/saveAudienceOpen elsewhere in this
    // file. Ignored entirely on desktop, where the picker stays inline.
    const [isSourceSheetOpen, setIsSourceSheetOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [selectedRows, setSelectedRows] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(false)

    const [audienceDataMaster, setAudienceDataMaster] = useState([])
    const [saveAudienceOpen, setSaveAudienceOpen] = useState(false)

    useEffect(() => {
        if (currentlySelectedSubCategory) getAudienceData();
    }, [currentlySelectedSubCategory]);

    // This screen now has its own fixed-bottom bar on mobile (the Audience
    // Data Master summary + Save) — same reasoning/pattern as the campaign
    // wizard (ComCreateCampaigns.js): the FAB's corner isn't free here, so
    // it's hidden for the whole screen rather than left to collide with it.
    useEffect(() => {
        window.dispatchEvent(new CustomEvent('layout:hideFab', { detail: { active: true } }))
        return () => {
            window.dispatchEvent(new CustomEvent('layout:hideFab', { detail: { active: false } }))
        }
    }, [])

    const [audienceDataFetched, setAudienceDataFetched] = useState([])
    // "Add Manually" — a second Custom data source alongside Upload Excel,
    // for when the client list is too short to bother with a CSV. Feeds
    // the exact same audienceDataFetched table/search/select/push flow as
    // the Excel and real-category branches; only how the rows get in is
    // different.
    const [manualPhoneNumbersInput, setManualPhoneNumbersInput] = useState('')

    const sampleData = [
        {
            id: uuidv4(),
            salutation: "Mr.",
            // storeIdentifier: "STR123456",
            clientStoreRelationIdentifier : 'SF34FDF',
            email: "example@example.com",
            countryCode: "+91",
            client_name: "John Doe",
            mobile: "9876543210",
            gender: "Male",
            address: "123 Street, City",
            currentStatus: ''
        },
    ];

    // Download sample CSV file
    const downloadSample = () => {
        const csv = Papa.unparse(sampleData);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "SampleClientData.csv";
        link.click();
    };

    // Custom sources (Upload Excel, Add Manually) have no backend category
    // to fetch — without excluding both here, switching into either one
    // still fell through to the real getAudienceData API call with a
    // bogus audienceTemplateIdentifier ('ExcelUpload'/'ManualEntry'), and
    // whatever the backend happened to return for that got shown as if it
    // were real data (the stale/wrong-looking rows seen when picking Add
    // Manually). Both branches should just start from an empty table.
    const isCustomSource = (identifier) => identifier === 'ExcelUpload' || identifier === 'ManualEntry';

    const getAudienceData = async () => {
        console.log(currentlySelectedSubCategory);
        if (!isCustomSource(currentlySelectedSubCategory?.audienceIdentifier)) {
            const getAudienceDataResponse = await fetch(BASE_URL_API + "getAudienceData", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    audienceTemplateIdentifier: currentlySelectedSubCategory?.audienceIdentifier,
                    storeIdentifier: activeStore?.storeIdentifier,
                }),
            });

            if (!getAudienceDataResponse.ok) {
                console.log("Something went wrong : Server Error");
            } else {
                const getAudienceDataRespo = await getAudienceDataResponse.json();
                if (getAudienceDataRespo.status === "success") {
                    setAudienceDataFetched(getAudienceDataRespo?.response);
                } else if (getAudienceDataRespo.message === "tokenExpired") {
                    authCtx.logout();
                } else {
                    setAudienceDataFetched([])
                }
                console.log(getAudienceDataRespo);
            }
        } else {
            setAudienceDataFetched([])
        }
    };

    // Handle Search
    const filteredData = audienceDataFetched.filter((item) =>
        Object.values(item).some((value) =>
            value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
    );

    // Handle Sorting
    const sortData = (key) => {
        const direction =
            sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
        const sortedData = [...filteredData].sort((a, b) => {
            if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
            if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
            return 0;
        });
        setSortConfig({ key, direction });
        setAudienceDataFetched(sortedData);
    };

    // Pagination
    const paginatedData = filteredData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    // react-select needs {value, label} options and the full selected
    // option object (not the raw number) — same rowsPerPage values as
    // before (including "All" resolving to the live fetched-row count),
    // just adapted to the shape this component expects. Two lists because
    // the real-category and Excel-upload branches already offered
    // different max page sizes below.
    const rowsPerPageOptionsFull = [10, 15, 20, 50, 100, 150, 200, 300, 400, 500, 600, 700, 800]
        .map((n) => ({ value: n, label: String(n) }))
        .concat([{ value: audienceDataFetched?.length, label: 'All' }])
    const rowsPerPageOptionsShort = [10, 15, 20, 50, 100, 150]
        .map((n) => ({ value: n, label: String(n) }))
        .concat([{ value: audienceDataFetched?.length, label: 'All' }])
    const selectedRowsPerPage = (options) => options.find((o) => o.value === rowsPerPage) || null

    // Row Selection
    const toggleRowSelection = (item) => {
        setSelectedRows((prev) => {
            // Check if the item is already selected
            const exists = prev.some((row) => row.clientStoreRelationIdentifier === item.clientStoreRelationIdentifier);
            if (exists) {
                // Remove the item if it is already selected
                return prev.filter((row) => row.clientStoreRelationIdentifier !== item.clientStoreRelationIdentifier);
            } else {
                // Add the item if it is not selected
                return [...prev, item];
            }
        });
    };

    // Select All Rows
    const toggleSelectAll = () => {
        if (selectedRows.length === paginatedData.length) {
            // Deselect all rows
            setSelectedRows([]);
        } else {
            // Select all rows on the current page
            setSelectedRows(paginatedData);
        }
    };

    // Check if a row is selected
    const isRowSelected = (item) => {
        return selectedRows.some((row) => row.clientStoreRelationIdentifier === item.clientStoreRelationIdentifier);
    };

    const pushToAudienceHandler = (newRows) => {
        setAudienceDataMaster((prevMaster) => {
            // Identify unique entries based on clientStoreRelationIdentifier
            const uniqueNewRows = newRows.filter(
                (row) => !prevMaster.some(
                    (existingRow) => existingRow.mobile === row.mobile
                )
            );

            // Combine new unique rows with the existing master
            const updatedMaster = [...prevMaster, ...uniqueNewRows];

            // Perform analytics
            const totalAdded = newRows.length; // Total rows in the new batch
            const uniqueAdded = uniqueNewRows.length; // Newly added unique rows
            const totalInMaster = updatedMaster.length; // Total in the master data
            const genderDistribution = updatedMaster.reduce(
                (acc, row) => {
                    acc[row.gender] = (acc[row.gender] || 0) + 1;
                    return acc;
                },
                {}
            );

            // Log analytics (or return them if needed)
            console.log({
                totalAdded,
                uniqueAdded,
                totalInMaster,
                genderDistribution,
            });

            // Return the updated master data
            return updatedMaster;
        });
    };


    // Handle file upload and parse CSV
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLoading(true);
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: (result) => {
                    const headers = Object.keys(sampleData[0]);
                    const uploadedHeaders = result.meta.fields;
                    const headersMatch = headers.every((header) =>
                        uploadedHeaders.includes(header)
                    );

                    if (headersMatch) {
                        const uniquePhoneNumbers = new Set();
                        const parsedData = result.data
                            .map((row) => ({ ...row, id: uuidv4(), clientStoreRelationIdentifier : uuidv4() }))
                            .filter((row) => {
                                // Check for duplicate phone numbers
                                if (row.mobile && !uniquePhoneNumbers.has(row.mobile)) {
                                    uniquePhoneNumbers.add(row.mobile);
                                    return true; // Include the row
                                }
                                return false; // Exclude the duplicate
                            });

                        console.log("parsedData : ", parsedData)
                        setAudienceDataFetched(parsedData);
                        // setFilteredData(parsedData);
                        setSelectedRows([]);
                        setCurrentPage(1);
                    } else {
                        toast.error("Uploaded file headers do not match the required format.");
                    }
                    setLoading(false);
                },
            });
        }
    };

    // Parse the manual-entry textarea (one 10-digit phone number per line)
    // into the same row shape handleFileUpload produces, then feed it into
    // audienceDataFetched the same way — a one-shot replace of the table,
    // not an append, matching how a CSV upload replaces it rather than
    // accumulating across uploads.
    const handleAddManualNumbers = () => {
        const lines = manualPhoneNumbersInput
            .split('\n')
            .map((line) => line.trim())
            .filter((line) => line !== '');

        if (lines.length === 0) {
            toast.error("Please enter at least one phone number.");
            return;
        }

        const uniquePhoneNumbers = new Set();
        const validRows = [];
        let invalidCount = 0;

        lines.forEach((line) => {
            const digitsOnly = line.replace(/\D/g, '');
            if (!/^\d{10}$/.test(digitsOnly)) {
                invalidCount += 1;
                return;
            }
            if (uniquePhoneNumbers.has(digitsOnly)) return;
            uniquePhoneNumbers.add(digitsOnly);
            validRows.push({
                id: uuidv4(),
                clientStoreRelationIdentifier: uuidv4(),
                client_name: '',
                mobile: digitsOnly,
                gender: '',
            });
        });

        if (validRows.length === 0) {
            toast.error("No valid 10-digit phone numbers found. Please check the numbers and try again.");
            return;
        }

        if (invalidCount > 0) {
            toast.warn(`${validRows.length} number(s) added. ${invalidCount} line(s) were skipped — each line must be a 10-digit phone number.`);
        } else {
            toast.success(`${validRows.length} number(s) added.`);
        }

        setAudienceDataFetched(validRows);
        setSelectedRows([]);
        setCurrentPage(1);
        setManualPhoneNumbersInput('');
    };

    // Handle API submission
    const handleSubmit = async () => {
        if (selectedRows.length === 0) {
            toast.error("No rows selected for submission.");
            return;
        }

        // addClientInBulk
        const addClientInBulkResponse = await fetch(BASE_URL_API + "addClientInBulk",
            {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    clientData: filteredData
                })
            });

        if (!addClientInBulkResponse.ok) {
            console.log("Something went wrong : Server Error!");
        } else {
            const addClientInBulkRespo = await addClientInBulkResponse.json();
            if (addClientInBulkRespo.status === "success") {
                // setCardDetails(addClientInBulkRespo.response);
                // setData(addClientInBulkRespo?.response)
                // setFilteredData(addClientInBulkRespo?.response)
            } else {
                if (addClientInBulkRespo.message === "tokenExpired") {
                    authCtx.logout();
                } else {
                    // setCardDetails([]);
                }
            }
            console.log(addClientInBulkRespo)
        }

        // Call your API with selectedRows
        console.log("Submitting data to API:", selectedRows);
        // alert("Data submitted successfully.");
    };



    if (activeStore === null) {
        return <div>Please select a store first!</div>;
    } else {
        return (
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.hTitle} >
                        <FaUsers className={styles.icon} size={24} /> Create Your Audience
                    </div>
                    <div className={styles.createCampaignBtn}>
                        <Link to={redirectPath || `/communications/create-campaign/as`}>
                            {redirectPath ? 'Go To Campaign' : 'Create Campaign'}
                        </Link>
                    </div>
                </div>
                {/* Mobile-only — opens the audience source picker as a bottom
                    sheet instead of it permanently occupying the top of the
                    screen. Hidden on desktop, where the picker is already a
                    permanent sidebar column. */}
                <button type="button" className={styles.mobileSourceTrigger} onClick={() => setIsSourceSheetOpen(true)}>
                    <span className={styles.mobileSourceTriggerLabel}>Audience source</span>
                    <span className={styles.mobileSourceTriggerChip}>
                        {currentlySelectedSubCategory?.audienceName || 'Choose a source'}
                        <IoChevronDownOutline size={13} />
                    </span>
                </button>
                {isSourceSheetOpen && <div className={styles.sheetBackdrop} onClick={() => setIsSourceSheetOpen(false)} />}
                {selectedRows?.length > 0 &&
                    <div className={styles.selectedDataPopup}>
                        <div className={styles.sdpContainer} >
                            <div>
                                {selectedRows?.length} / {audienceDataFetched?.length} rows selected!
                            </div>
                            <div>
                                <button onClick={() => pushToAudienceHandler(selectedRows)} >Push to Audience</button>
                            </div>
                        </div>
                    </div>
                }
                {saveAudienceOpen && <SaveAudience isOpen={saveAudienceOpen} data={audienceDataMaster} onClose={() => setSaveAudienceOpen(false)} />}
                <div className={styles.columnsWrapper}>
                    {/* Desktop: a permanent sidebar column (unchanged).
                        Mobile: the exact same content becomes a bottom sheet
                        (see CreateAudience.module.css) — position:fixed and
                        translated off-screen until isSourceSheetOpen, so
                        there's one copy of this list, not two. */}
                    <div className={`${styles.leftColumnWrapper} ${isSourceSheetOpen ? styles.leftColumnWrapperOpen : ''}`}>
                        <div className={styles.sheetHandleBar} />
                        <div className={styles.sheetHeaderRow}>
                            <div className={styles.mobileSectionLabel}>Choose an audience source</div>
                            <button type="button" className={styles.sheetCloseBtn} onClick={() => setIsSourceSheetOpen(false)} aria-label="Close">
                                <MdClose size={18} />
                            </button>
                        </div>
                        <div className={styles.leftColumnContainer}>
                            <div>
                                {audienceData?.map((item, index) => (
                                    <EachAudienceCategory
                                        key={index}
                                        item={item}
                                        currentlySelectedSubCategory={currentlySelectedSubCategory}
                                        subCategorySelectHandler={(sub) => { subCategorySelectHandler(sub); setIsSourceSheetOpen(false); }}
                                    />
                                ))}
                                <div
                                    className={styles.headerWrapper}
                                    onClick={() => setIsOpen(!isOpen)}
                                    title={isOpen ? "Collapse" : "Expand"}
                                >
                                    <div className={styles.categoryTitle}>
                                        Custom
                                    </div>
                                    <motion.div
                                        initial={{ rotate: isOpen ? 0 : 180 }}
                                        animate={{ rotate: isOpen ? 180 : 0 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        {isOpen ? <IoChevronDownOutline size={20} /> : <IoChevronUpOutline size={20} />}
                                    </motion.div>
                                </div>

                                {/* Subcategories */}
                                <AnimatePresence >
                                    {isOpen && (
                                        <motion.div
                                            className={styles.audienceCategory}
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.3, ease: "easeInOut" }}
                                        >
                                            {[
                                                { audienceIdentifier: 'ExcelUpload', audienceName: 'Upload Excel' },
                                                { audienceIdentifier: 'ManualEntry', audienceName: 'Add Manually' },
                                            ]?.map((itemInner, indexInner) => (
                                                <motion.div
                                                    key={indexInner}
                                                    className={styles.subCategory}
                                                    onClick={() => { subCategorySelectHandler(itemInner); setIsSourceSheetOpen(false); }}
                                                    whileHover={{ scale: 1.01 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    transition={{ duration: 0.1 }}
                                                >
                                                    {currentlySelectedSubCategory?.audienceIdentifier === itemInner?.audienceIdentifier ? (
                                                        <MdRadioButtonChecked title="Selected" className={styles.radioButtonIcon} />
                                                    ) : (
                                                        <MdRadioButtonUnchecked title="Select Audience" className={styles.radioButtonIcon} />
                                                    )}
                                                    <span className={styles.audienceName}>{itemInner?.audienceName}</span>
                                                </motion.div>
                                            ))}
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                            </div>
                        </div>
                        <div className={styles.sheetFooter}>
                            <button type="button" className={styles.sheetFooterBtn} onClick={() => setIsSourceSheetOpen(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                    {!currentlySelectedSubCategory ?
                        /* Nothing picked yet — a "Selected audience (0)" card
                           with an empty description and a live search/table
                           for zero rows was actively confusing (real user
                           feedback), not just visual clutter. Show one clear
                           prompt instead of a half-populated version of the
                           real UI. */
                        <div className={styles.centerColumnWrapper}>
                            <div className={styles.emptySourceState}>
                                <FaUsers className={styles.emptySourceIcon} />
                                <div className={styles.emptySourceTitle}>Pick an audience source to get started</div>
                                <div className={styles.emptySourceSubtitle}>Choose a category to load recipients you can add to your audience.</div>
                                <button type="button" className={styles.emptySourceCta} onClick={() => setIsSourceSheetOpen(true)}>
                                    Choose Audience Source
                                </button>
                            </div>
                        </div>
                        : currentlySelectedSubCategory?.audienceIdentifier === "ManualEntry" ?
                        <div className={styles.centerColumnWrapper}>
                            <div className={styles.campaignHeader}>
                                <div className={styles.leftHeader}>
                                    <div className={styles.mobileSectionLabel}>Selected audience</div>
                                    <div className={styles.audienceName}>
                                        {currentlySelectedSubCategory?.audienceName} ({audienceDataFetched?.length})
                                    </div>
                                    <div className={styles.audienceDescription}>
                                        {currentlySelectedSubCategory?.audienceDescription}
                                    </div>

                                </div>
                                {/* No "Get Data" here either, same reasoning
                                    as the Excel branch — nothing to fetch for
                                    manually-typed numbers. */}
                            </div>
                            <div className={styles.uploadSection}>
                                <div className={styles.uploadSectionTitle}>Add Recipients Manually</div>
                                <div className={styles.uploadSectionSubtitle}>
                                    Enter one 10-digit phone number per line, then add them to the list below.
                                </div>
                                <textarea
                                    className={styles.manualEntryTextarea}
                                    placeholder={"9876543210\n9876543211\n9876543212"}
                                    value={manualPhoneNumbersInput}
                                    onChange={(e) => setManualPhoneNumbersInput(e.target.value)}
                                    rows={5}
                                />
                                <div className={styles.manualEntryActions}>
                                    <button type="button" className={styles.submitBtn} onClick={handleAddManualNumbers}>
                                        Add Numbers
                                    </button>
                                </div>
                            </div>

                            <div>
                                <div className={styles.toolbar}>
                                    <div className={styles.searchBoxWrapper}>
                                        <input
                                            type="text"
                                            className={styles.searchBox}
                                            placeholder="Search..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                    <div className={styles.paginationControls}>
                                        <span className={styles.paginationControlsLabel}>Rows per page:</span>
                                        <Select
                                            options={rowsPerPageOptionsShort}
                                            value={selectedRowsPerPage(rowsPerPageOptionsShort)}
                                            onChange={(option) => setRowsPerPage(Number(option.value))}
                                            styles={rowsPerPageSelectStyles}
                                            isSearchable={false}
                                            menuPlacement="auto"
                                        />
                                    </div>

                                    <div className={styles.pagination}>
                                        <button
                                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                        >
                                            Previous
                                        </button>
                                        <span>
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                                <div className={styles.table}>
                                    <div className={styles.tableHeader}>
                                        {paginatedData?.length > 0 &&
                                            <div className={styles.checkBox} onClick={() => toggleSelectAll()} >
                                                {selectedRows.length === paginatedData.length ? <MdOutlineCheckBox size={20} /> : <MdCheckBoxOutlineBlank size={20} />}
                                            </div>
                                        }
                                        <div className={styles.clientName} onClick={() => sortData("client_name")}>
                                            Name <FaSort />
                                        </div>
                                        <div className={styles.mobile} onClick={() => sortData("mobile")}>
                                            Phone <FaSort />
                                        </div>
                                        <div className={styles.gender} onClick={() => sortData("gender")}>
                                            Gender <FaSort />
                                        </div>
                                        <div className={styles.totalSpent} >Spend</div>
                                        <div className={styles.totalVisits}>Visits</div>
                                        <div>Action</div>
                                    </div>
                                    {paginatedData.length > 0 ? (
                                        <div className={styles.tableData}>
                                            {paginatedData.map((item, index) => (
                                                <div className={styles.eachDataRow} key={index}>
                                                    <div className={styles.checkBox} onClick={() => toggleRowSelection(item)} >
                                                        {isRowSelected(item) ?
                                                            <MdOutlineCheckBox size={20} />
                                                            :
                                                            <MdCheckBoxOutlineBlank size={20} />
                                                        }
                                                    </div>
                                                    <div className={styles.clientName} onClick={() => toggleRowSelection(item)} >{item?.client_name}</div>
                                                    <div className={styles.mobile} onClick={() => toggleRowSelection(item)} >+91-{item?.mobile}</div>
                                                    {/* Forces Gender/Spend/Visits onto their own line together —
                                                        without a real flex sibling here, giving .gender a 100%
                                                        flex-basis directly would consume the whole line itself
                                                        and push Spend/Visits down to a third line instead. */}
                                                    <div className={styles.rowBreak} />
                                                    <div className={styles.gender} data-label="Gender" onClick={() => toggleRowSelection(item)}>{item?.gender}</div>
                                                    <div className={styles.totalSpent} data-label="Spend">
                                                        {item?.TotalSpent}
                                                    </div>
                                                    <div className={styles.totalVisits} data-label="Visits">
                                                        {item?.TotalVisits}
                                                    </div>
                                                    <div className={styles.actionCell}>
                                                        <a
                                                            href={`/user/${item?.clientStoreRelationIdentifier}`}
                                                            target="_blank"
                                                        >
                                                            View Details
                                                        </a>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className={styles.tableData}>
                                            <div className={styles.noRecordsFound}>No records found!</div>
                                        </div>
                                    )}
                                </div>


                            </div>
                        </div>
                        : currentlySelectedSubCategory?.audienceIdentifier !== "ExcelUpload" ?
                        <div className={styles.centerColumnWrapper}>
                            <div className={styles.campaignHeader}>
                                <div className={styles.leftHeader}>
                                    <div className={styles.mobileSectionLabel}>Selected audience</div>
                                    <div className={styles.audienceName}>
                                        {currentlySelectedSubCategory?.audienceName} ({audienceDataFetched?.length})
                                    </div>
                                    <div className={styles.audienceDescription}>
                                        {currentlySelectedSubCategory?.audienceDescription}
                                    </div>

                                </div>
                                <div className={styles.rightHeader}>
                                    <button className={styles.getDataBtn} onClick={getAudienceData}>
                                        Get Data
                                    </button>
                                </div>
                            </div>
                            <div>
                                <div className={styles.toolbar}>
                                    <div className={styles.searchBoxWrapper}>
                                        <input
                                            type="text"
                                            className={styles.searchBox}
                                            placeholder="Search..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                    <div className={styles.paginationControls}>
                                        <span className={styles.paginationControlsLabel}>Rows per page:</span>
                                        <Select
                                            options={rowsPerPageOptionsFull}
                                            value={selectedRowsPerPage(rowsPerPageOptionsFull)}
                                            onChange={(option) => setRowsPerPage(Number(option.value))}
                                            styles={rowsPerPageSelectStyles}
                                            isSearchable={false}
                                            menuPlacement="auto"
                                        />
                                    </div>

                                    <div className={styles.pagination}>
                                        <button
                                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                        >
                                            Previous
                                        </button>
                                        <span>
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                                <div className={styles.table}>
                                    <div className={styles.tableHeader}>
                                        {paginatedData?.length > 0 &&
                                            <div className={styles.checkBox} onClick={() => toggleSelectAll()} >
                                                {selectedRows.length === paginatedData.length ? <MdOutlineCheckBox size={20} /> : <MdCheckBoxOutlineBlank size={20} />}
                                            </div>
                                        }
                                        <div className={styles.clientName} onClick={() => sortData("client_name")}>
                                            Name <FaSort />
                                        </div>
                                        <div className={styles.mobile} onClick={() => sortData("mobile")}>
                                            Phone <FaSort />
                                        </div>
                                        <div className={styles.gender} onClick={() => sortData("gender")}>
                                            Gender <FaSort />
                                        </div>
                                        <div className={styles.totalSpent} >Spend</div>
                                        <div className={styles.totalVisits}>Visits</div>
                                        <div>Action</div>
                                    </div>
                                    {paginatedData.length > 0 ? (
                                        <div className={styles.tableData}>
                                            {paginatedData.map((item, index) => (
                                                <div className={styles.eachDataRow} key={index}>
                                                    <div className={styles.checkBox} onClick={() => toggleRowSelection(item)} >
                                                        {isRowSelected(item) ?
                                                            <MdOutlineCheckBox size={20} />
                                                            :
                                                            <MdCheckBoxOutlineBlank size={20} />
                                                        }
                                                    </div>
                                                    <div className={styles.clientName} onClick={() => toggleRowSelection(item)} >{item?.client_name}</div>
                                                    <div className={styles.mobile} onClick={() => toggleRowSelection(item)} >+91-{item?.mobile}</div>
                                                    {/* Forces Gender/Spend/Visits onto their own line together —
                                                        without a real flex sibling here, giving .gender a 100%
                                                        flex-basis directly would consume the whole line itself
                                                        and push Spend/Visits down to a third line instead. */}
                                                    <div className={styles.rowBreak} />
                                                    <div className={styles.gender} data-label="Gender" onClick={() => toggleRowSelection(item)}>{item?.gender}</div>
                                                    <div className={styles.totalSpent} data-label="Spend">
                                                        {item?.TotalSpent}
                                                    </div>
                                                    <div className={styles.totalVisits} data-label="Visits">
                                                        {item?.TotalVisits}
                                                    </div>
                                                    <div className={styles.actionCell}>
                                                        <a
                                                            href={`/user/${item?.clientStoreRelationIdentifier}`}
                                                            target="_blank"
                                                        >
                                                            View Details
                                                        </a>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className={styles.tableData}>
                                            <div className={styles.noRecordsFound}>No records found!</div>
                                        </div>
                                    )}
                                </div>


                            </div>
                        </div>
                        :
                        <div className={styles.centerColumnWrapper}>
                            <div className={styles.campaignHeader}>
                                <div className={styles.leftHeader}>
                                    <div className={styles.mobileSectionLabel}>Selected audience</div>
                                    <div className={styles.audienceName}>
                                        {currentlySelectedSubCategory?.audienceName} ({audienceDataFetched?.length})
                                    </div>
                                    <div className={styles.audienceDescription}>
                                        {currentlySelectedSubCategory?.audienceDescription}
                                    </div>

                                </div>
                                {/* No "Get Data" here — unlike a real category,
                                    there's nothing to fetch for a manual
                                    upload; that button only resets the table
                                    to empty in this branch, which reads as
                                    confusing rather than useful. */}
                            </div>
                            <div className={styles.uploadSection}>
                                <div className={styles.uploadSectionTitle}>Upload Client Data in Bulk</div>
                                <div className={styles.uploadSectionSubtitle}>
                                    Download the sample sheet, fill it in with your client data, then upload it below.
                                </div>
                                <div className={styles.uploadActions}>
                                    <div className={styles.initSection}>
                                        <button className={styles.downloadBtn} onClick={downloadSample}>
                                            Download Sample Sheet
                                        </button>
                                        <input
                                            type="file"
                                            accept=".csv"
                                            onChange={handleFileUpload}
                                            className={styles.fileUpload}
                                        />
                                    </div>
                                    <div>
                                        <button className={styles.submitBtn} onClick={handleSubmit}>
                                            Submit Selected Rows
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <div className={styles.toolbar}>
                                    <div className={styles.searchBoxWrapper}>
                                        <input
                                            type="text"
                                            className={styles.searchBox}
                                            placeholder="Search..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                    <div className={styles.paginationControls}>
                                        <span className={styles.paginationControlsLabel}>Rows per page:</span>
                                        <Select
                                            options={rowsPerPageOptionsShort}
                                            value={selectedRowsPerPage(rowsPerPageOptionsShort)}
                                            onChange={(option) => setRowsPerPage(Number(option.value))}
                                            styles={rowsPerPageSelectStyles}
                                            isSearchable={false}
                                            menuPlacement="auto"
                                        />
                                    </div>

                                    <div className={styles.pagination}>
                                        <button
                                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                        >
                                            Previous
                                        </button>
                                        <span>
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                                <div className={styles.table}>
                                    <div className={styles.tableHeader}>
                                        {paginatedData?.length > 0 &&
                                            <div className={styles.checkBox} onClick={() => toggleSelectAll()} >
                                                {selectedRows.length === paginatedData.length ? <MdOutlineCheckBox size={20} /> : <MdCheckBoxOutlineBlank size={20} />}
                                            </div>
                                        }
                                        <div className={styles.clientName} onClick={() => sortData("client_name")}>
                                            Name <FaSort />
                                        </div>
                                        <div className={styles.mobile} onClick={() => sortData("mobile")}>
                                            Phone <FaSort />
                                        </div>
                                        <div className={styles.gender} onClick={() => sortData("gender")}>
                                            Gender <FaSort />
                                        </div>
                                        <div className={styles.totalSpent} >Spend</div>
                                        <div className={styles.totalVisits}>Visits</div>
                                        <div>Action</div>
                                    </div>
                                    {paginatedData.length > 0 ? (
                                        <div className={styles.tableData}>
                                            {paginatedData.map((item, index) => (
                                                <div className={styles.eachDataRow} key={index}>
                                                    <div className={styles.checkBox} onClick={() => toggleRowSelection(item)} >
                                                        {isRowSelected(item) ?
                                                            <MdOutlineCheckBox size={20} />
                                                            :
                                                            <MdCheckBoxOutlineBlank size={20} />
                                                        }
                                                    </div>
                                                    <div className={styles.clientName} onClick={() => toggleRowSelection(item)} >{item?.client_name}</div>
                                                    <div className={styles.mobile} onClick={() => toggleRowSelection(item)} >+91-{item?.mobile}</div>
                                                    {/* Forces Gender/Spend/Visits onto their own line together —
                                                        without a real flex sibling here, giving .gender a 100%
                                                        flex-basis directly would consume the whole line itself
                                                        and push Spend/Visits down to a third line instead. */}
                                                    <div className={styles.rowBreak} />
                                                    <div className={styles.gender} data-label="Gender" onClick={() => toggleRowSelection(item)}>{item?.gender}</div>
                                                    <div className={styles.totalSpent} data-label="Spend">
                                                        {item?.TotalSpent}
                                                    </div>
                                                    <div className={styles.totalVisits} data-label="Visits">
                                                        {item?.TotalVisits}
                                                    </div>
                                                    <div className={styles.actionCell}>
                                                        <a
                                                            href={`/user/${item?.clientStoreRelationIdentifier}`}
                                                            target="_blank"
                                                        >
                                                            View Details
                                                        </a>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className={styles.tableData}>
                                            <div className={styles.noRecordsFound}>No records found!</div>
                                        </div>
                                    )}
                                </div>


                            </div>
                        </div>
                    }

                    {/* On mobile this becomes a persistent bottom summary bar
                        (like a native app's cart bar) instead of a section
                        buried at the end of a long scroll — same data, same
                        Save handler, just repositioned via CSS. */}
                    <div className={styles.rightColumnWrapper}>
                        <div className={styles.rightColumnContainer} >
                            <div className={styles.adSummaryTextBlock}>
                                <div className={styles.adSummaryTitle}>
                                    Audience Data Master
                                </div>
                                <div className={styles.adSummaryCountRow}>
                                    <div className={styles.adSummaryCount}>{audienceDataMaster?.length}</div>
                                    <div className={styles.adSummaryCountLabel}>Audience Size</div>
                                </div>
                            </div>
                            {audienceDataMaster?.length > 0 &&
                                <button className={styles.button} onClick={() => setSaveAudienceOpen(true)} >
                                    <FaSave className={styles.icon} /> Save Audience
                                </button>
                            }
                        </div>
                    </div>
                </div>
            </div>
        );
    }
};

export default CreateAudience;
